import { PARAMETER_TYPE } from "./components/table/interfaces";

export enum ANALYTICS_VIEW {
  CHART = 0,
  TABLE = 1,
}

export enum ANALYTICS_MEASURE_TYPE {
  TREND = 0,
  SPECTRUM = 1,
  SIGNAL_FORM = 2,
  ORBIT = 3,
}

export enum ANALYTICS_OBJECT {
  TRAIN = 0,
  MECHANISM = 1,
  NODE = 2,
  POINT = 3,
}

export enum TRAIN_ROTATION_DIRECTION {
  UNDEFINED = -1,
  COUNTER_CLOCKWISE = 0,
  CLOCKWISE = 1,
}

export interface IComponentFaultFrequencyLineApi {
  lineKey: string;
  displayName: string;
  frequencyHz: number;
  coeffToBase: number;
}

export interface IFaultFrequencyChartCursor {
  frequencyHz: number;
  color: string;
}

export interface IComponentFaultFrequencyApi {
  idComponent: number;
  idTrainComponent?: number;
  idTrainMechanism?: number | null;
  trainComponentName?: string;
  mechanismName?: string;
  componentName: string;
  freqCode: string;
  freqDescription: string;
  baseValue: number;
  type: number;
  length: number;
  isVisible: boolean;
  isExtra: boolean;
  code: number;
  frequencyHz: number;
  coeffToBase: number;
  displayName: string;
  lines: IComponentFaultFrequencyLineApi[] | null;
}

export interface IGetCommonParamsDataValues {
  name: string;
  url: string;
  data: any;
  type: PARAMETER_TYPE;
}

interface ICommonParamsResponsePascal {
  idTrain: number;
  Date: string;
  paramID: number;
  Value: number;
}

interface ICommonParamsResponseCamel {
  idtrain: number;
  date: string;
  paramid: number;
  value: number;
}

export type TCommonParamsResponse = ICommonParamsResponsePascal | ICommonParamsResponseCamel;

export interface ICommonParamsResponse {
  idTrain: number;
  Date: string;
  paramID: number;
  Value: number;
}

export interface IGetCommonPointsDataValues {
  type: PARAMETER_TYPE;
  measureName: string;
  data: any;
  url: string;
}

export interface IGetPointMeasuresDataValues {
  type: PARAMETER_TYPE;
  pointName: string;
  points: number[];
  measureName: string;
  startTimestamp: string;
  endTimestamp: string;
}

export interface IGetLastMeasuresValues {
  measureName: string;
  data: any;
  url: string;
}

export interface IGetSignalDataValues {
  measureType: ANALYTICS_MEASURE_TYPE;
  pointName: string;
  measures: Array<{
    idMeasure: number;
    date: string;
  }>;
}

export interface IGetOrbitDataValues {
  idMeasure: number;
  date: string;
}

export interface ITrainDetailsRotationResponse {
  RotationDirection: TRAIN_ROTATION_DIRECTION;
}

type GetCommonParametersData = { idTrain: number } | { idTrainMech: number } | { idTrainComponent: number };

export interface IGetCommonParametersArgs {
  keyphasorsUrl: string;
  parametersUrl: string;
  pointsUrl: string;
  data: GetCommonParametersData;
}

export interface IMechanismAutocompleteResponse {
  id: number;
  title: string;
  description: string | null;
}

export interface IComponentAutocompleteResponse {
  id: number;
  title: string;
  idTrainMechanism: number | null;
  MechanismName: string | null;
}

export interface IAutocompletePointResponse {
  id: number | string;
  title: string;
  Direction: number;
}

export interface IPointAutocompleteResponse extends IAutocompletePointResponse {
  usn: number;
}

export interface IMechanismPointAutocompleteResponse extends IAutocompletePointResponse {
  Description: string | null;
}

export interface IComponentPointAutocompleteResponse extends IAutocompletePointResponse {
  Description: string | null;
}

export interface IKeyphasorAutocompleteResponse {
  id: number | null;
  title: string | null;
  description: string | null;
}

export interface IParameterAutocompleteResponse {
  id: number | null;
  title: string | null;
  description: string | null;
}

export interface IMeasureAutocompleteResponse {
  title: string | null;
  description: string | null;
  useValue2: boolean;
  type: number | null;
  rangeType: number | null;
  ids: number[];
}

export interface IMeasuresResponse {
  idMeasure: number;
  Date: string;
  Value1: number;
  Value2: number | null;
  idPoint: number;
  Units: number;
  Name: string;
  Description?: string | null;
  AlarmLevel2: number | null;
  AlarmLevel3: number | null;
  AlarmLevel4: number | null;
  AlarmType: number;
  RPM: number | null;
  Param1?: number | null;
  Param2?: number | null;
}

export interface IObjectOption {
  id: number | string;
  title: string;
  hint?: string;
  points?: IPointAutocompleteResponse[];
}

export interface IParameterOption {
  id: number | string;
  title: string;
  group: PARAMETER_TYPE;
  useValue2?: boolean;
}

export interface IMeasureOption {
  id: number;
  title: string;
  idMeasure?: number;
  measureDate?: string;
}

export interface IOptionResponse {
  id: number | string;
  title: string;
  description?: string | null;
  idTrainMechanism?: number | null;
  useValue2?: boolean;
  idMeasure?: number;
  measureDate?: string;
}

export interface IOption extends IOptionResponse {
  hint?: string | null;
  group?: string;
}

export interface IOptionGroup {
  group: string;
  options: IOption[];
}

export interface IPointResponse {
  id: number;
  title: string;
  Direction: number;
}

export interface ILastMeasureDataAutocompleteResponse {
  idMeasure: number;
  idPoint: number;
  measureDate: string;
  Description?: string | null;
  value1: number;
  units: number;
  param1?: number | null;
  param2?: number | null;
}

interface ILastMeasureGroup {
  name: string;
  data: ILastMeasureDataAutocompleteResponse[];
}

export type TLastMeasureAutocompleteResponse = Record<string, ILastMeasureGroup>;

export interface ISignalDataResponse {
  idMeasure: number;
  pointName: string;
  pointDescription: string | null;
  date: string;
  value1: number;
  value2: number | null;
  idPoint: number;
  description: string | null;
  units: number;
  rangeType: number;
  resolution: number | null;
  alarmType: number;
  rotationFrequencyRpm: number | null;
  name: string;
  xStart: number;
  xEnd: number;
  data: number[];
}

export interface IOrbitDataResponse {
  idMeasure: number;
  date: string;
  units: number;
  name: string;
  resolution: number;
  param3: number;
  param3DataMs?: number | null;
  rangeType: number;
  rotationFrequencyHz: number | null;
  xPickupAngle: number | null;
  yPickupAngle: number | null;
  Xmax: number;
  Ymax: number;
  Smax: number;
  xData: number[];
  yData: number[];
  zeroIndexes: number[];
}

export interface IAnalyticsForm {
  measureType: number;
  object: number | null;
  idObject: number | null;
  idParameter: number | string | Array<number | string> | null;
  idMeasure: number | number[] | null;
  idObjectsOptions: IObjectOption[];
  idParametersOptions: IParameterOption[];
  idMeasuresOptions: IMeasureOption[];
}

export interface IItem {
  date: string;
  value: number;
  rotationFrequencyRpm?: number | null;
}

export interface IChartData {
  parameterName?: string;
  parameterLabel?: string;
  idMeasure?: number;
  idTrain?: number;
  paramId?: number;
  idPoint?: number;
  pointDirection?: number | null;
  color: string;
  name: string;
  items: IItem[];
  units: string;
  unitCode?: number | null;
  alarmLevel2?: number | null;
  alarmLevel3?: number | null;
  alarmLevel4?: number | null;
  isAmplitudePhase?: boolean;
}

export interface ISignalItem {
  x: number;
  y: { real?: number; imag?: number } | number;
}

export interface IChartDataSignal {
  idMeasure?: number;
  pointName?: string;
  pointDescription?: string | null;
  date?: string;
  value1?: number;
  value2?: number | null;
  idPoint?: number;
  measurementName?: string;
  measurementDescription?: string | null;
  rangeType?: number;
  resolution?: number | null;
  alarmType?: number;
  rotationFrequencyRpm?: number | null;
  color: string;
  name: string;
  items: ISignalItem[];
  unitsX: string;
  unitsY: string;
  unitsXCode?: number | null;
  unitsYCode?: number | null;
}

export interface IOrbitItem {
  index: number;
  x: number;
  y: number;
  theta: number;
  r: number;
  i?: number;
  isZero?: boolean;
  date?: Date;
  rotationFrequencyRpm?: number | null;
}

export interface IChartDataOrbit {
  seriesKey?: string;
  parameterName?: string;
  parameterLabel?: string;
  idPoint?: number;
  color: string;
  name: string;
  items: IOrbitItem[];
  units: string;
  unitCode?: number | null;
  Xmax?: number;
  Ymax?: number;
  Smax?: number;
  xData?: number[];
  yData?: number[];
  zeroIndexes?: number[];
  resolution?: number;
  param3?: number;
  param3DataMs?: number | null;
  rangeType?: number;
  rotationFrequencyHz?: number | null;
  xPickupAngle?: number | null;
  yPickupAngle?: number | null;
}
